/**
 * @file TriggerButtonView.tsx
 * @description Memoized trigger button view component for the player selection dropdown.
 * Renders a pressable button with the selected player name and chevron icon.
 */
import { ChevronsUpDown } from "lucide-react-native";
import { memo } from "react";

import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";

/**
 * Props for TriggerButtonView component
 */
export interface TriggerButtonViewProps {
  restTriggerProps: Record<string, unknown>;
  onPress: (event: unknown) => void;
  selectedComparisonPlayerLabel: string;
}

/**
 * View component for TriggerButton
 * @param props - Component props
 * @param props.restTriggerProps - Props to spread on the pressable
 * @param props.onPress - Press event handler
 * @param props.selectedComparisonPlayerLabel - Label to display in the button
 */
const TriggerButtonView = ({
  restTriggerProps,
  onPress,
  selectedComparisonPlayerLabel,
}: TriggerButtonViewProps) => (
  <Pressable
    {...restTriggerProps}
    onPress={onPress}
    className="flex-1 flex-row items-center gap-2"
  >
    <Heading variant="display-sm" className="flex-1 text-left" isTruncated>
      {selectedComparisonPlayerLabel}
    </Heading>
    <Icon as={ChevronsUpDown} className="shrink-0" />
  </Pressable>
);

TriggerButtonView.displayName = "TriggerButtonView";

export default memo(TriggerButtonView);
